import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Lock, Pencil } from "lucide-react";
import { Input } from "@/shared/components/Input";
import { CampoDataNascimento } from "@/shared/components/CampoDataNascimento";
import { Button } from "@/shared/components/Button";
import { dataHojeInput } from "@/shared/lib/datas";
import { isoParaTexto } from "@/shared/lib/dataDigitada";
import { rotuloSexo } from "@/shared/lib/sexo";
import { useAtualizarMeusDados } from "@/core/hooks/useUsuarios";
import type { Pessoa } from "@/core/services/pessoasService";

// Campos editáveis ganham borda visível: sem ela, o fundo do campo quase some no cartão (02/11/2026).
const CLASSE_CAMPO = "ring-1 ring-muted/50";

const BOTAO_SECUNDARIO =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-border bg-transparent px-4 py-2.5 text-sm font-semibold text-text transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60";

// Mesmo limite da coluna (pessoas.telefone varchar(20)) e do formulário do administrador.
const LIMITE_TELEFONE = 20;

const schema = z.object({
  telefone: z.string().max(LIMITE_TELEFONE, `Telefone pode ter no máximo ${LIMITE_TELEFONE} caracteres.`),
  dataNascimento: z
    .string()
    .min(1, "Informe sua data de nascimento.")
    .refine((v) => v <= dataHojeInput(), "A data de nascimento não pode ser no futuro."),
});

type Valores = z.infer<typeof schema>;

/** "27999999888" -> "(27) 99999-9888". Outro formato (ou vazio) volta como está / null. */
function telefoneParaExibir(valor: string | null | undefined): string | null {
  const v = (valor ?? "").trim();
  if (!v) return null;
  const d = v.replace(/\D/g, "");
  if (d.length === 11 && d.length === v.length) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10 && d.length === v.length) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v;
}

function LinhaInformacao({
  rotulo,
  valor,
  vazio = "—",
}: {
  rotulo: string;
  valor: string | null | undefined;
  vazio?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="shrink-0 text-sm text-muted">{rotulo}</dt>
      <dd className="min-w-0 break-words text-right text-sm font-semibold text-text">
        {valor || vazio}
      </dd>
    </div>
  );
}

/**
 * "Minhas informações" do Meu perfil (02/11/2026). A própria pessoa altera só **telefone** e
 * **data de nascimento**; e-mail, CPF, nome e sexo continuam com a administração da igreja.
 * Isso não depende só da tela: o banco recusa qualquer outra coluna quando a pessoa edita o
 * próprio cadastro (migration 0115, trigger `protege_campos_sensiveis_pessoas`).
 *
 * A tela abre **fechada** (só leitura): os campos só aparecem depois de tocar em "Editar", com
 * "Cancelar" e "Salvar alterações" (pedido do responsável, 02/11/2026).
 */
export function FormularioMinhasInformacoes({ pessoa }: { pessoa: Pessoa }) {
  const atualizar = useAtualizarMeusDados();
  const [editando, setEditando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  const valoresSalvos: Valores = {
    telefone: pessoa.telefone ?? "",
    dataNascimento: pessoa.data_nascimento ?? "",
  };

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<Valores>({ resolver: zodResolver(schema), defaultValues: valoresSalvos });

  // Depois de salvar, o cadastro é recarregado: o formulário passa a considerar o novo valor
  // como o "salvo" (o botão volta a ficar desligado).
  useEffect(() => {
    reset({ telefone: pessoa.telefone ?? "", dataNascimento: pessoa.data_nascimento ?? "" });
  }, [pessoa.telefone, pessoa.data_nascimento, reset]);

  // Encadeia o onChange de limpeza (só dígitos) sem perder o do react-hook-form.
  const registroTelefone = register("telefone");

  function abrirEdicao() {
    setErro(null);
    setSalvo(false);
    setEditando(true);
  }

  function cancelarEdicao() {
    reset(valoresSalvos);
    setErro(null);
    setEditando(false);
  }

  async function onSubmit(dados: Valores) {
    setErro(null);
    setSalvo(false);
    try {
      await atualizar.mutateAsync({
        id: pessoa.id,
        telefone: dados.telefone.trim() === "" ? null : dados.telefone.trim(),
        dataNascimento: dados.dataNascimento,
      });
      setSalvo(true);
      setEditando(false);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível salvar. Tente de novo.");
    }
  }

  const avisoAdministracao = (
    <p className="flex items-start gap-1.5 text-xs text-muted">
      <Lock size={13} aria-hidden="true" className="mt-0.5 shrink-0" />
      Nome, CPF, e-mail e sexo só a administração da igreja altera.
    </p>
  );

  if (!editando) {
    return (
      <div className="flex flex-col gap-4">
        <dl className="divide-y divide-border">
          <LinhaInformacao
            rotulo="Telefone"
            valor={telefoneParaExibir(pessoa.telefone)}
            vazio="Não informado"
          />
          <LinhaInformacao
            rotulo="Data de nascimento"
            valor={isoParaTexto(pessoa.data_nascimento)}
            vazio="Não informada"
          />
          <LinhaInformacao rotulo="E-mail" valor={pessoa.email} />
          <LinhaInformacao rotulo="Sexo" valor={rotuloSexo(pessoa.sexo) ?? "Não informado"} />
        </dl>

        {salvo && (
          <p role="status" className="rounded-md bg-success/10 px-3 py-2 text-sm font-medium text-success">
            Dados salvos.
          </p>
        )}

        <button type="button" onClick={abrirEdicao} className={`${BOTAO_SECUNDARIO} w-full`}>
          <Pencil size={16} aria-hidden="true" />
          Editar telefone e data de nascimento
        </button>
        {avisoAdministracao}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <Input
        label="Telefone"
        placeholder="(00) 00000-0000"
        inputMode="tel"
        maxLength={LIMITE_TELEFONE}
        className={CLASSE_CAMPO}
        erro={errors.telefone?.message}
        {...registroTelefone}
        onChange={(e) => {
          e.target.value = e.target.value.replace(/\D/g, "").slice(0, 11);
          void registroTelefone.onChange(e);
        }}
      />

      <Controller
        name="dataNascimento"
        control={control}
        render={({ field }) => (
          <CampoDataNascimento
            label="Data de nascimento"
            name={field.name}
            ref={field.ref}
            value={field.value ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            className={CLASSE_CAMPO}
            erro={errors.dataNascimento?.message}
          />
        )}
      />

      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={cancelarEdicao}
          disabled={atualizar.isPending}
          className={BOTAO_SECUNDARIO}
        >
          Cancelar
        </button>
        <Button type="submit" carregando={atualizar.isPending} disabled={!isDirty}>
          Salvar alterações
        </Button>
      </div>
      {avisoAdministracao}
    </form>
  );
}
